/**
 * Disziplin Sprint: feste Strecke, gemessen wird die Zeit.
 *
 * Die Uhr startet mit der ersten Bewegung, nicht mit dem Aufruf des Screens -
 * sonst wuerde das Lesen der Anleitung Zeit kosten. Der Zieleinlauf wird
 * innerhalb des letzten Scroll-Schritts interpoliert, damit die Zeit an der
 * Leistung haengt und nicht an der Bildwiederholrate (siehe SprintRun).
 */

import { useCallback, useMemo, useReducer, useRef, useState } from 'react'
import { Header } from '../components/Header'
import { LiveValue } from '../components/LiveValue'
import { ResultPanel } from '../components/ResultPanel'
import { ScrollTrack } from '../components/ScrollTrack'
import type { TapeMarker } from '../components/drawTape'
import { useDevice } from '../DeviceContext'
import { SPRINT_DISTANCES, SprintRun, type SprintDistanceId } from '../engine/disciplines'
import { loadRecords, recordKey } from '../engine/records'
import type { ScrollTracker } from '../engine/scrollTracker'
import { formatDistanceInline, formatTime } from '../engine/units'

export interface SprintChallengeProps {
  /** Gesetzt, wenn der Lauf die Antwort auf ein Duell ist. */
  challengeCode?: string
  /** Im Duell steht die Distanz fest, sie wird nicht mehr ausgewaehlt. */
  forcedDistanceId?: SprintDistanceId
  /** Zeit des Herausforderers - laeuft als Schattenlaeufer auf der Bahn mit. */
  rivalTimeMs?: number
  rivalName?: string
  onAnswered?: () => void
}

export function SprintScreen(props: SprintChallengeProps = {}) {
  const [distanceId, setDistanceId] = useState<SprintDistanceId | null>(
    props.forcedDistanceId ?? null,
  )
  const distance = SPRINT_DISTANCES.find((entry) => entry.id === distanceId)

  if (!distance) {
    return <DistancePicker onPick={setDistanceId} />
  }

  return (
    <SprintTrack
      key={distance.id}
      distanceId={distance.id}
      targetCm={distance.cm}
      label={distance.label}
      onLeave={props.forcedDistanceId ? null : () => setDistanceId(null)}
      challenge={props}
    />
  )
}

function DistancePicker({ onPick }: { onPick: (id: SprintDistanceId) => void }) {
  const records = useMemo(loadRecords, [])

  return (
    <div className="screen screen--page" data-discipline="sprint">
      <Header title="Sprint" />
      <div className="page">
        <p className="page__lead">Feste Strecke, laufende Uhr.</p>
        <p className="page__note">Die Zeit beginnt mit deinem ersten Scroll, nicht vorher.</p>

        <div className="distances">
          {SPRINT_DISTANCES.map((entry) => {
            const best = records.best[recordKey('sprint', entry.id)]
            return (
              <button
                key={entry.id}
                className="distance"
                type="button"
                onClick={() => onPick(entry.id)}
              >
                <span className="distance__label">{entry.label}</span>
                <span className="distance__best" data-empty={best ? undefined : 'true'}>
                  {best ? `${formatTime(best.timeMs)} s` : '—'}
                </span>
                <span className="distance__caption">{best ? 'deine Bestzeit' : 'noch offen'}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

interface SprintTrackProps {
  distanceId: SprintDistanceId
  targetCm: number
  label: string
  /** null im Duell: die Distanz ist dort vorgegeben. */
  onLeave: (() => void) | null
  challenge: SprintChallengeProps
}

function SprintTrack({ distanceId, targetCm, label, onLeave, challenge }: SprintTrackProps) {
  const { metrics } = useDevice()
  const trackerRef = useRef<ScrollTracker | null>(null)
  const [run, setRun] = useState<SprintRun | null>(null)
  const [, rerender] = useReducer((count: number) => count + 1, 0)

  const handleTracker = useCallback(
    (tracker: ScrollTracker) => {
      trackerRef.current = tracker
      setRun(new SprintRun(tracker, targetCm, rerender))
    },
    [targetCm, rerender],
  )

  /**
   * Marken werden hier pro Bild berechnet, nicht einmal pro Render: Der
   * Schattenlaeufer des Gegners bewegt sich waehrend des Laufs. Seine Position
   * ergibt sich aus seiner Zeit, gleichmaessige Geschwindigkeit unterstellt -
   * mehr gibt eine gespeicherte Endzeit nicht her, und es reicht, um zu sehen,
   * ob man vorn oder hinten liegt.
   */
  const rivalTimeMs = challenge.rivalTimeMs
  const rivalName = challenge.rivalName ?? 'Gegner'
  const markers = useCallback((): TapeMarker[] => {
    const result: TapeMarker[] = [{ cm: targetCm, label: `Ziel ${label}` }]
    if (rivalTimeMs && rivalTimeMs > 0 && run && run.phase === 'laeuft') {
      const ghostCm = Math.min(targetCm, targetCm * (run.currentTimeMs / rivalTimeMs))
      result.push({ cm: ghostCm, label: rivalName })
    }
    return result
  }, [targetCm, label, rivalTimeMs, rivalName, run])

  const liveTime = useCallback(() => formatTime(run?.currentTimeMs ?? 0), [run])
  const liveRemaining = useCallback(() => {
    const done = trackerRef.current?.forwardCm ?? 0
    return formatDistanceInline(Math.max(0, targetCm - done))
  }, [targetCm])

  const phase = run?.phase ?? 'bereit'

  return (
    <div className="screen" data-discipline="sprint">
      <Header title={`Sprint ${label}`} />
      <ScrollTrack metrics={metrics} onTracker={handleTracker} markers={markers} referenceRatio={0.34}>
        {phase === 'bereit' && (
          <div className="hud hud--center">
            <p className="hud__value hud__value--muted">{formatTime(0)}</p>
            <p className="hud__hint">Auf die Plätze. Die Uhr startet beim ersten Scroll.</p>
          </div>
        )}

        {phase === 'laeuft' && (
          <div className="hud hud--center">
            <LiveValue get={liveTime} className="hud__value" />
            <p className="hud__hint">
              noch <LiveValue get={liveRemaining} />
            </p>
            {run && run.splits.length > 0 && (
              <ul className="splits">
                {run.splits.map((split) => (
                  <li key={split.fraction}>
                    <span>{split.label}</span>
                    <span>{formatTime(split.timeMs)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {phase === 'ziel' && run && (
          <ResultPanel
            result={run.toResult(distanceId)}
            headline={`${formatTime(run.finishTimeMs)} s`}
            subline={sprintSubline(label, targetCm, run.finishTimeMs, rivalTimeMs, rivalName)}
            challengeCode={challenge.challengeCode}
            onChallengeAnswered={challenge.onAnswered}
            onRetry={() => run.reset()}
          />
        )}
      </ScrollTrack>

      {phase === 'bereit' && onLeave && (
        <button className="track__leave" type="button" onClick={onLeave}>
          Andere Distanz
        </button>
      )}
    </div>
  )
}

function sprintSubline(
  label: string,
  targetCm: number,
  timeMs: number,
  rivalTimeMs: number | undefined,
  rivalName: string,
): string {
  const base = `${label} · Ø ${averageSpeedLabel(targetCm, timeMs)}`
  if (!rivalTimeMs || rivalTimeMs <= 0) return base
  const diff = (rivalTimeMs - timeMs) / 1000
  const verdict =
    diff > 0
      ? `${formatSeconds(diff)} s schneller als ${rivalName}`
      : `${formatSeconds(-diff)} s langsamer als ${rivalName}`
  return `${base} · ${verdict}`
}

function averageSpeedLabel(distanceCm: number, timeMs: number): string {
  if (timeMs <= 0) return '–'
  const kmh = distanceCm / 100_000 / (timeMs / 3_600_000)
  return `${kmh.toLocaleString('de-DE', { maximumFractionDigits: 1 })} km/h`
}

function formatSeconds(seconds: number): string {
  return seconds.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
